import app from 'flarum/forum/app';
import IndexPage from 'flarum/forum/components/IndexPage';
import Page from 'flarum/common/components/Page';
import ItemList from 'flarum/common/utils/ItemList';
import extractText from 'flarum/common/utils/extractText';
import Button from 'flarum/common/components/Button';
import LinkButton from 'flarum/common/components/LinkButton';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { byId, changelogFor, lineColor, photoFile, load, loaded, meta, wikiUrl } from '../cameraData';
import { downloadCounts } from '../downloadCounts';
import { trunkBuilds, autobuildUrl, trunkBuild } from '../trunkBuilds';
import { cameraCard } from './CameraCard';
import AdSlot from '../adSlot';
import CameraChangelogModal from './CameraChangelogModal';

// Canon's own version string, and how to read it off the camera. ver.req /
// vers.req does not work on every model and has no clean cutoff, so the FAQ
// is the honest answer rather than a recipe of our own.
const FW_VERSION_FAQ =
  'https://chdk.fandom.com/wiki/FAQ#Q._How_can_I_get_the_original_firmware_version_number_of_my_camera?';

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes > 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  return Math.round(bytes / 1024) + ' KB';
}

/**
 * One camera: identity first (photo fallback, name, aliases, port state),
 * then the mirrored downloads, then firmware history and the
 * "my firmware version is missing" guidance.
 *
 * Like CamerasIndexPage it extends IndexPage, so the page sits in the
 * discussion-list pane with the side nav beside it instead of going full
 * width; only contentItems() is ours.
 */
export default class CameraDetailPage extends IndexPage {
  oninit(vnode) {
    // NOT super.oninit(): IndexPage.oninit calls app.discussions.refreshParams(),
    // a full GET /api/discussions this page never renders (one Flarum boot per
    // cold hit, 2026-08-23), and files this url in app.history as 'index'.
    // Page.oninit plus the two layout fields is everything the view needs.
    Page.prototype.oninit.call(this, vnode);
    this.bodyClass = 'App--index';
    this.scrollTopOnCreate = false;
    app.history.push('cameras', extractText(app.translator.trans('acseven-chdk-cameras.forum.index.title')));

    // null until load() resolves, same as it is for a genuinely unknown id --
    // hence the `loaded` guard in cameraContent(), so a cold page does not
    // flash "unknown camera" before the data arrives.
    this.camera = null;
    this.copied = null;

    load().then(() => {
      this.camera = byId[m.route.param('id')] || null;
      this.setTitle();
      m.redraw();
    });
    // null until the staff-only request lands (and stays null for everyone
    // else), which is also the "do not render a count" signal
    this.counts = null;

    // Firmware the development branch has for this camera; [] until the feed
    // lands, and [] for good if it never does.
    this.trunk = [];

    trunkBuilds().then((map) => {
      this.trunk = map[m.route.param('id')] || [];
      m.redraw();
    });

    downloadCounts().then((counts) => {
      if (Object.keys(counts).length) {
        this.counts = counts;
        m.redraw();
      }
    });

    // Discussions where this camera was mentioned, from the CAMERAKW/CAMERA
    // tags the corpus reparse baked into the stored post XML. null = still
    // loading (header + spinner); [] = known-empty or failed (section hidden).
    this.mentions = null;

    app
      .request({ url: app.forum.attribute('apiUrl') + '/chdk-cameras/mentions/' + m.route.param('id') })
      .then(
        (res) => {
          this.mentions = res.discussions || [];
          m.redraw();
        },
        () => {
          this.mentions = [];
          m.redraw();
        }
      );
  }

  /**
   * Staff-only photo controls: replace/add the photo at runtime (stored as
   * an override, no rebuild), or drop the override back to the bundled
   * default. A plain page reload after each action keeps the state machine
   * to zero lines.
   */
  photoControls(camera) {
    const overridden = !!(app.forum.attribute('chdkCamerasPhotoOverrides') || {})[camera.id];
    const busy = this.uploading;

    return (
      <div className="CameraDetailPage-photoControls">
        <Button
          className="Button Button--text"
          icon="fas fa-camera"
          loading={busy}
          onclick={() => this.photoInput(camera).click()}
        >
          {app.translator.trans(
            photoFile(camera)
              ? 'acseven-chdk-cameras.forum.detail.change_photo'
              : 'acseven-chdk-cameras.forum.detail.add_photo'
          )}
        </Button>
        {overridden && (
          <Button
            className="Button Button--text"
            icon="fas fa-undo"
            loading={busy}
            onclick={() => {
              this.uploading = true;
              app
                .request({ method: 'DELETE', url: app.forum.attribute('apiUrl') + '/chdk-cameras/photo/' + camera.id })
                .then(() => window.location.reload());
            }}
          >
            {app.translator.trans('acseven-chdk-cameras.forum.detail.restore_default_photo')}
          </Button>
        )}
      </div>
    );
  }

  // hidden <input type=file>, created once so repeated picks work
  photoInput(camera) {
    if (!this._photoInput) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png';
      input.onchange = () => {
        const file = input.files && input.files[0];
        input.value = ''; // allow re-picking the same file
        if (!file) return;
        const body = new FormData();
        body.append('photo', file);
        this.uploading = true;
        app
          .request({
            method: 'POST',
            url: app.forum.attribute('apiUrl') + '/chdk-cameras/photo/' + camera.id,
            body,
          })
          .then(
            () => window.location.reload(),
            () => {
              this.uploading = false;
              m.redraw();
            }
          );
        m.redraw();
      };
      this._photoInput = input;
    }
    return this._photoInput;
  }

  setTitle() {
    app.setTitle(this.camera ? this.camera.name : extractText(app.translator.trans('acseven-chdk-cameras.forum.index.title')));
    app.setTitleCount(0);
  }

  contentItems() {
    const items = new ItemList();

    items.add('camera', this.cameraContent());

    return items;
  }

  cameraContent() {
    if (!loaded) return <LoadingIndicator className="CameraDetailPage-loading" />;

    if (!this.camera) {
      return (
        <div className="CameraDetailPage CameraDetailPage--missing">
          <h2>{app.translator.trans('acseven-chdk-cameras.forum.detail.unknown_camera')}</h2>
          <LinkButton href={app.route('cameras')} icon="fas fa-arrow-left">
            {app.translator.trans('acseven-chdk-cameras.forum.detail.back_to_index')}
          </LinkButton>
        </div>
      );
    }

    const camera = this.camera;
    const changelog = changelogFor(camera);
    // Both branches name their own version: the mirror's tag, and whatever the
    // autobuild built last (it says so in the feed we already fetched).
    const stable = app.translator.trans('acseven-chdk-cameras.forum.detail.stable_column', {
      version: (meta && meta.build && meta.build.version) || '',
    });
    const dev = app.translator.trans('acseven-chdk-cameras.forum.detail.dev_column', {
      version: (trunkBuild && trunkBuild.version) || '1.7',
    });
    // Once per render, not once per call site: Mithril redraws are global, so
    // any click anywhere in the app re-runs this view.
    const rows = this.firmwareRows();

    return (
      <div className="CameraDetailPage">
        <nav className="CameraDetailPage-crumb">
          <LinkButton href={app.route('cameras')} icon="fas fa-arrow-left" className="Button--link">
            {app.translator.trans('acseven-chdk-cameras.forum.detail.back_to_index')}
          </LinkButton>
        </nav>

        <header className="CameraDetailPage-hero">
          {cameraCard(camera, { detail: true, count: rows.length })}
          {app.forum.attribute('chdkCamerasStaff') && this.photoControls(camera)}
        </header>

        {/* Only where the wiki has a page: the link is a resolved title, not
            a guess off the name (99 of 161 names 404). */}
        {wikiUrl(camera) && (
          <div className="CameraDetailPage-wiki">
            <a className="CameraDetailPage-wikiLink" href={wikiUrl(camera)} target="_blank" rel="noopener">
              <i className="fas fa-book" /> {app.translator.trans('acseven-chdk-cameras.forum.detail.wiki_page', { name: camera.name })}
            </a>
          </div>
        )}

        {/* Zone `camera_downloads` in the ad extension, position `custom`.
            Renders nothing at all until an ad is put in it. */}
        <AdSlot zone="camera_downloads" />

        <section className="CameraDetailPage-section">
          <h3 className="CameraDetailPage-sectionTitle">
            {app.translator.trans('acseven-chdk-cameras.forum.detail.downloads')}
          </h3>

          {camera.firmware.length === 0 && this.trunk.length === 0 ? (
            <p className="CameraDetailPage-noFirmware">
              {app.translator.trans('acseven-chdk-cameras.forum.detail.no_stable_build', {
                name: camera.name,
              })}
            </p>
          ) : (
            <>
              {/* The version is a property of the camera, not a choice: no
                  "latest" marker, and the how-to-read-it link sits above the
                  table, where someone who does not know theirs will see it. */}
              <p className="CameraDownloads-match">
                {app.translator.trans('acseven-chdk-cameras.forum.detail.match_version')}{' '}
                <a href={FW_VERSION_FAQ} target="_blank" rel="noopener">
                  {app.translator.trans('acseven-chdk-cameras.forum.detail.find_version')}
                </a>
              </p>

              {/* The column heads carry the branch version, because that is
                  the real choice in this table: 1.6.1 is the released build,
                  1.7 is what the autobuild made this morning. */}
              <table className="CameraDownloads Table">
                <thead>
                  <tr>
                    <th>{app.translator.trans('acseven-chdk-cameras.forum.detail.build')}</th>
                    <th>{stable}</th>
                    {this.trunk.length > 0 && <th className="CameraDownloads-dev">{dev}</th>}
                  </tr>
                </thead>
                <tbody>
                  {/* One row per Canon firmware version, newest first. Only the
                      full build gets a button -- the small zip is the same
                      binaries without scripts, curves and readme, so it only
                      updates an existing install; it rides along as a quiet
                      link instead of a column that competes for the click. */}
                  {rows.map((row) => {
                    const status = (row.stable && row.stable.status) || (row.dev && row.dev.status);

                    return (
                      <tr className={m.route.param('fw') === row.id ? 'CameraDownloads-row--match' : ''}>
                        <td className="CameraDownloads-version">
                          <b>{row.id}</b>
                          {/* Set by "Identify by photo": the version read out of
                              the owner's own photo, so the right row is obvious. */}
                          {m.route.param('fw') === row.id && (
                            <span className="CameraDownloads-yours">
                              {app.translator.trans('acseven-chdk-cameras.forum.detail.from_photo')}
                            </span>
                          )}
                          {status && (
                            <span
                              className={'CameraDownloads-status CameraDownloads-status--' + status}
                              title={extractText(app.translator.trans('acseven-chdk-cameras.forum.detail.status_help'))}
                            >
                              {status}
                            </span>
                          )}
                        </td>
                        {/* data-label: on a phone the table stacks and the
                            column heads are gone, so each cell says its own. */}
                        <td data-label={extractText(stable)}>
                          {row.stable ? this.downloadCell(row.stable.full) : <span className="DownloadCell-none">—</span>}
                        </td>
                        {this.trunk.length > 0 && (
                          <td className="CameraDownloads-dev" data-label={extractText(dev)}>
                            {row.dev ? (
                              this.downloadCell(row.dev.full, true)
                            ) : (
                              <span className="DownloadCell-none">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          {this.developmentNote(camera)}

          <p className="CameraDetailPage-installer">
            {app.translator.trans('acseven-chdk-cameras.forum.help.installer_body')}
          </p>

          {changelog.length > 0 && (
            <Button
              className="Button CameraDetailPage-changelog"
              icon="fas fa-history"
              onclick={() => app.modal.show(CameraChangelogModal, { camera })}
            >
              {app.translator.trans('acseven-chdk-cameras.forum.detail.firmware_history')}
            </Button>
          )}
        </section>

        <section className="CameraDetailPage-section CameraDetailPage-help">
          <h3 className="CameraDetailPage-sectionTitle">
            {app.translator.trans('acseven-chdk-cameras.forum.help.missing_fw_title')}
          </h3>
          <p>{app.translator.trans('acseven-chdk-cameras.forum.help.missing_fw_body')}</p>
        </section>

        {(this.mentions === null || this.mentions.length > 0) && (
          <section className="CameraDetailPage-section">
            <h3 className="CameraDetailPage-sectionTitle">
              {app.translator.trans('acseven-chdk-cameras.forum.detail.mentioned_in')}
            </h3>
            {this.mentions === null ? (
              <LoadingIndicator size="small" className="CameraMentions-loading" />
            ) : (
              <ul className="CameraMentions">
                {/* most-mentioning discussions first, capped server-side at 20;
                    "posts" is the number of mentioning posts in the thread */}
                {this.mentions.map((d) => (
                  <li className="CameraMentions-item">
                    <a className="CameraMentions-link" href={app.route('discussion', { id: d.id })}>
                      {d.title}
                    </a>
                    <span className="CameraMentions-count">
                      {app.translator.trans('acseven-chdk-cameras.forum.detail.mentioned_posts', { count: d.posts })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    );
  }

  /**
   * The line under the table: what the 1.7 column is, and the way back to the
   * autobuild when a rebuild has replaced the file this page linked. Two
   * cameras get a sentence more -- on DIGIC 6/7 bodies 1.7 is the better
   * choice even where a 1.6.1 build exists, and where there is no stable
   * build at all it is the only one.
   */
  developmentNote(camera) {
    if (!this.trunk.length) return null;

    const key = camera.digic >= 60 ? 'dev_digic' : camera.firmware.length ? null : 'dev_no_stable';

    return (
      <p className="CameraDownloads-devNote">
        {key && <span>{app.translator.trans('acseven-chdk-cameras.forum.detail.' + key, { name: camera.name })} </span>}
        {app.translator.trans('acseven-chdk-cameras.forum.detail.dev_source', {
          revision: (trunkBuild && trunkBuild.revision) || '',
        })}{' '}
        <a href={autobuildUrl(camera)} target="_blank" rel="noopener">
          {app.translator.trans('acseven-chdk-cameras.forum.detail.dev_page')}
        </a>
      </p>
    );
  }

  /**
   * One row per firmware version the camera has anywhere: a stable build, a
   * 1.7 autobuild, or both. Newest first.
   */
  firmwareRows() {
    const dev = {};
    this.trunk.forEach((fw) => (dev[fw.id] = fw));

    const ids = this.camera.firmware.map((fw) => fw.id);
    this.trunk.forEach((fw) => ids.indexOf(fw.id) === -1 && ids.push(fw.id));

    return ids
      .sort((a, b) => b.localeCompare(a))
      .map((id) => ({
        id,
        stable: this.camera.firmware.find((fw) => fw.id === id) || null,
        dev: dev[id] || null,
      }));
  }

  /**
   * One build: the button, then size and digest beneath it. `dev` is the 1.7
   * column -- same anatomy, secondary button, so the released build stays the
   * obvious click. Download counts are GitHub's and only exist for mirrored
   * files.
   *
   * ponytail: the small "update only" zip is deliberately not linked. It is
   * 400kb less for the same binaries, minus updates to the bundled scripts --
   * an easier-looking click that is rarely the right one (reyalp, 2026-08).
   */
  downloadCell(file, dev) {
    if (!file) {
      return <span className="DownloadCell-none">—</span>;
    }

    return (
      <div className="DownloadCell">
        <a className={'Button DownloadCell-button' + (dev ? ' DownloadCell-button--dev' : ' Button--primary')} href={file.url}>
          {app.translator.trans('acseven-chdk-cameras.forum.detail.download')}
        </a>
        <span className="DownloadCell-meta">
          <span className="DownloadCell-size">{humanSize(file.size)}</span>
          <a
            className="DownloadCell-hash"
            href="#"
            title={extractText(app.translator.trans('acseven-chdk-cameras.forum.detail.copy_hash'))}
            onclick={(e) => {
              e.preventDefault();
              navigator.clipboard && navigator.clipboard.writeText(file.sha256);
              this.copied = file.sha256;
            }}
          >
            {this.copied === file.sha256 ? app.translator.trans('acseven-chdk-cameras.forum.detail.copied') : file.sha256}
          </a>
        </span>
        {this.counts && !dev && (
          <span className="DownloadCell-count" title={extractText(app.translator.trans('acseven-chdk-cameras.forum.stats.count_title'))}>
            <i className="fas fa-download" /> {(this.counts[file.file] || 0).toLocaleString()}
          </span>
        )}
      </div>
    );
  }
}
